"use client";

/**
 * An inline script that runs while the browser parses the HTML — before the
 * first paint, and before React exists.
 *
 * The `type` is the whole point. On the server it is `text/javascript`, so the
 * browser executes it as it parses. On the client it is `text/plain`, which
 * React treats as a data block rather than as code — without that, React 19
 * warns in development ("Encountered a script tag while rendering React
 * component"), because a script React creates client-side never executes and it
 * assumes you expected otherwise. Here we do not: this script has already done
 * its work by the time React runs, and re-running it would be pointless.
 *
 * `suppressHydrationWarning` covers the `type` differing between the two.
 *
 * It has to be a CLIENT component for that to work at all. As a server
 * component the ternary is evaluated once, on the server, and `text/javascript`
 * is baked into the RSC payload — the browser then renders that verbatim and
 * React warns exactly as before. Only a client component re-evaluates
 * `typeof window` in the browser. It still server-renders, so the HTML the
 * browser parses carries `text/javascript` and the script runs as it always
 * did; the bundle cost is this one function.
 *
 * The pattern is Next's own — see
 * `node_modules/next/dist/docs/01-app/02-guides/preventing-flash-before-hydration.md`,
 * "Extracting a reusable component". `next/script` is NOT an alternative for
 * anything that must run before paint: for an inline script it wraps the body
 * in `self.__next_s.push(...)` and lets its own runtime execute it after load,
 * which is exactly the flash this avoids.
 */
export default function InlineScript({ html }: { html: string }) {
  return (
    <script
      type={typeof window === "undefined" ? "text/javascript" : "text/plain"}
      suppressHydrationWarning
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
